import { Copy, Plus } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { EmptyState } from "../components/EmptyState";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import { useSectionSearch } from "../lib/section-search";
import { teamColumns, toTeamRecord } from "../lib/management-collection-records";
import { isOfflineQueued } from "../lib/offline-link";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";

type TeamRole = "owner" | "manager" | "collaborator";

export function TeamPage() {
  const utils = trpc.useUtils();
  const team = trpc.team.list.useQuery();
  const update = trpc.team.updateAccess.useMutation({ onSuccess: () => void utils.team.list.invalidate() });
  const invite = trpc.auth.invite.useMutation();
  const [open, setOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [viewMode, setViewMode] = useViewMode("squadra");
  const [search, setSearch] = useSectionSearch();
  const records = useMemo(
    () => (team.data ?? []).map((member, index) => toTeamRecord(member, (input) => update.mutate({ userId: member.userId, ...input }), index)),
    [team.data, update],
  );
  const filteredRecords = useMemo(
    () => records.filter((record) => matchesCollectionSearch(search, [record.title, ...Object.values(record.cells).map((cell) => cell?.text)])),
    [records, search],
  );

  async function submitInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const result = await invite.mutateAsync({
      email: String(data.get("email")),
      role: String(data.get("role")) as TeamRole,
      canViewAllStructures: data.get("canViewAllStructures") === "on",
    });
    // Il link di invito lo firma il server: senza rete non esiste ancora niente da copiare.
    if (isOfflineQueued(result)) {
      setInviteUrl(null);
      setInviteNotice("Invito salvato sul dispositivo. Il link arriva appena torna la rete.");
      return;
    }
    setInviteNotice(null);
    setInviteUrl(`${window.location.origin}/accetta-invito?token=${encodeURIComponent(result.token)}`);
  }

  return (
    <main className="page">
      <header className="page-heading"><div><p className="eyebrow">Persone e accessi</p><h1>La squadra</h1><p>Decidi chi gestisce il lavoro e quali strutture può vedere.</p></div><div className="page-heading__actions"><CollectionSearch label="Cerca nella squadra" value={search} onChange={setSearch} placeholder="Nome, email, ruolo o struttura" /><ViewModeToggle value={viewMode} onChange={setViewMode} /><button className="button button--primary" type="button" onClick={() => { setOpen(!open); setInviteUrl(null); setInviteNotice(null); }}><Plus size={18} /> Invita persona</button></div></header>
      {open && <form className="editor-card editor-card--compact" onSubmit={submitInvite}>
        <div className="editor-card__heading"><h2>Invita nella squadra</h2><p>Scegli subito ruolo e visibilità. Il link resta valido per sette giorni.</p></div>
        <div className="form-grid">
          <label><span>Email</span><input name="email" type="email" required placeholder="collaboratore@email.it" /></label>
          <label><span>Ruolo</span><select name="role" defaultValue="collaborator"><option value="collaborator">Collaboratore</option><option value="manager">Responsabile</option><option value="owner">Titolare</option></select></label>
          <label className="check-control form-grid__wide"><input name="canViewAllStructures" type="checkbox" /> Può vedere tutte le strutture</label>
        </div>
        {invite.error && <p className="form-error">{invite.error.message}</p>}
        {inviteNotice && <p className="notice" role="status">{inviteNotice}</p>}
        {inviteUrl && <div className="invite-link"><span><strong>Invito pronto</strong><small>Copia e invia questo link alla persona.</small></span><input readOnly value={inviteUrl} aria-label="Link invito" /><button className="button button--outline" type="button" onClick={() => void navigator.clipboard.writeText(inviteUrl)}><Copy size={17} /> Copia</button></div>}
        <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setOpen(false)}>Chiudi</button>{!inviteUrl && <button className="button button--primary" disabled={invite.isPending}>Crea invito</button>}</div>
      </form>}
      <section className="collection-section" data-view={viewMode}>
        {team.isLoading && <p className="loading-copy">Caricamento squadra...</p>}
        {!team.isLoading && team.isError && <div className="query-error" role="alert"><strong>Squadra non disponibile</strong><button className="button button--outline" type="button" onClick={() => void team.refetch()}>Riprova</button></div>}
        {!team.isLoading && !team.isError && records.length === 0 && <EmptyState title="Squadra vuota">Invita la prima persona per iniziare a collaborare.</EmptyState>}
        {!team.isLoading && !team.isError && records.length > 0 && filteredRecords.length === 0 && <EmptyState title="Nessun risultato">Prova a modificare la ricerca.</EmptyState>}
        {!team.isLoading && !team.isError && filteredRecords.length > 0 && <CollectionView mode={viewMode} ariaLabel="Squadra" titleLabel="Persona" columns={teamColumns} records={filteredRecords} print={{ section: "Squadra", details: [search ? `Ricerca: ${search}` : null] }} />}
      </section>
      {update.error && <p className="form-error">{update.error.message}</p>}
    </main>
  );
}
